/**
 * Auth state management using Zustand.
 *
 * Handles:
 * - Login/logout
 * - Token storage
 * - Token refresh
 * - User session
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  username?: string;  // Optional for backward compatibility
  full_name?: string;
  role: 'clinician' | 'admin' | 'audit';
  email_verified: boolean;
}

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  mfaRequired: boolean;
  tempToken: string | null;

  // Email auth state
  otpRequired: boolean;
  otpEmail: string | null;

  // Actions
  login: (username: string, password: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signupWithEmail: (email: string, password: string, fullName?: string) => Promise<void>;
  verifyOtp: (email: string, otpCode: string) => Promise<void>;
  resendOtp: (email: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      mfaRequired: false,
      tempToken: null,
      otpRequired: false,
      otpEmail: null,

      // Login action
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Login failed');
          }

          const data = await response.json();

          // Check if MFA is required
          if (data.mfa_required) {
            set({
              mfaRequired: true,
              tempToken: data.temp_token,
              isLoading: false,
            });
            return;
          }

          // Login successful
          set({
            user: data.user,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            tempToken: null,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Verify MFA action
      verifyMfa: async (code: string) => {
        set({ isLoading: true, error: null });

        const { tempToken } = get();
        if (!tempToken) {
          set({ error: 'No temporary token available', isLoading: false });
          throw new Error('No temporary token');
        }

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/mfa/verify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              temp_token: tempToken,
              code,
            }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'MFA verification failed');
          }

          const data = await response.json();

          set({
            user: data.user,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
            mfaRequired: false,
            tempToken: null,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'MFA verification failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Email signup with OTP
      signupWithEmail: async (email: string, password: string, fullName?: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/signup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password, full_name: fullName }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Signup failed');
          }

          const data = await response.json();

          // OTP sent - user needs to verify
          set({
            otpRequired: true,
            otpEmail: email,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Signup failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Email login with OTP support
      loginWithEmail: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/login/email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Login failed');
          }

          const data = await response.json();

          // Check if OTP is required
          if (data.otp_required) {
            set({
              otpRequired: true,
              otpEmail: email,
              isLoading: false,
            });
            return;
          }

          // Login successful - tokens received
          set({
            user: data.user,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
            otpRequired: false,
            otpEmail: null,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Verify OTP and complete login/signup
      verifyOtp: async (email: string, otpCode: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/verify-otp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, otp_code: otpCode }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'OTP verification failed');
          }

          const data = await response.json();

          // OTP verified - login complete
          set({
            user: data.user,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
            isLoading: false,
            otpRequired: false,
            otpEmail: null,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'OTP verification failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Resend OTP
      resendOtp: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/resend-otp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to resend OTP');
          }

          set({ isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to resend OTP',
            isLoading: false,
          });
          throw error;
        }
      },

      // Logout action
      logout: () => {
        // Call logout endpoint
        const { accessToken } = get();
        if (accessToken) {
          fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }).catch(console.error); // Fire and forget
        }

        // Clear state
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          mfaRequired: false,
          tempToken: null,
          otpRequired: false,
          otpEmail: null,
        });
      },

      // Refresh access token
      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });

          if (!response.ok) {
            // Refresh token invalid or expired - logout
            get().logout();
            throw new Error('Session expired');
          }

          const data = await response.json();

          // Update tokens (token rotation)
          set({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          });
        } catch (error) {
          get().logout();
          throw error;
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Check auth status on app load
      checkAuth: async () => {
        const { accessToken, refreshToken } = get();

        if (!accessToken || !refreshToken) {
          set({ isAuthenticated: false });
          return;
        }

        // Try to refresh token to verify it's still valid
        try {
          await get().refreshAccessToken();
          set({ isAuthenticated: true });
        } catch {
          set({ isAuthenticated: false });
        }
      },
    }),
    {
      name: 'auth-storage', // LocalStorage key
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

/**
 * Helper hook to get current user
 */
export const useCurrentUser = () => useAuthStore((state) => state.user);

/**
 * Helper hook to check if user has required role
 */
export const useHasRole = (roles: string[]) => {
  const user = useCurrentUser();
  return user ? roles.includes(user.role) : false;
};
